import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../lib/auth";
import { ThemeToggle } from "../../components/ThemeToggle";
import { APIError, ErrCode } from "../../client";

type LocationState = { from?: { pathname: string } } | null;

const AdminLoginPage = () => {
  const { state, login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  // If already signed in, jump straight to the dashboard.
  useEffect(() => {
    if (state.status === "authenticated") {
      navigate("/admin", { replace: true });
    }
  }, [state, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await login(password);
      const from = (location.state as LocationState)?.from?.pathname ?? "/admin";
      navigate(from, { replace: true });
    } catch (err) {
      if (err instanceof APIError && err.code === ErrCode.Unauthenticated) {
        setError("Incorrect password.");
      } else if (err instanceof APIError) {
        setError(`${err.code}: ${err.message}`);
      } else if (err instanceof Error) {
        setError(err.message || "Sign in failed.");
      } else {
        setError("Sign in failed.");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col theme-transition">
      <main className="flex-1 flex items-center justify-center px-6 py-16">
        <form
          onSubmit={handleSubmit}
          className="w-full max-w-sm border border-[var(--color-kj-border)] bg-[var(--color-kj-surface)] px-8 py-10 text-center"
        >
          <h1 className="font-display text-2xl uppercase-track mb-1">Admin</h1>
          <p className="text-xs uppercase-track text-[var(--color-kj-text-light)] mb-8">
            Kinsey Jones
          </p>

          <label className="block text-left">
            <span className="text-[10px] uppercase-track text-[var(--color-kj-text-light)]">
              Password
            </span>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoFocus
              className="mt-1 w-full bg-transparent border-b border-[var(--color-kj-border)] focus:border-[var(--color-kj-text)] outline-none py-2 text-sm transition-colors"
            />
          </label>

          {error && (
            <p className="mt-4 text-xs uppercase-track text-[var(--color-kj-danger)]">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={busy || password === ""}
            className="mt-8 w-full font-display uppercase-track text-xs px-6 py-3 border border-[var(--color-kj-text)] hover:bg-[var(--color-kj-text)] hover:text-[var(--color-kj-bg)] transition-colors disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
          >
            {busy ? "Signing in…" : "Sign in"}
          </button>
        </form>
      </main>
      <footer className="px-8 py-8 text-center">
        <div className="flex justify-center">
          <ThemeToggle />
        </div>
      </footer>
    </div>
  );
};

export default AdminLoginPage;
